import { Directive, ElementRef, Injectable, Input, OnChanges, OnDestroy, OnInit, Optional, Renderer2 } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { Task, TaskField } from 'src/app/data/model/task';

/**
 * The approval in progress on the record a side panel shows, if any: which task, and which of
 * the record's fields it is about.
 *
 * Provided by the component that owns the record (`providers: [RecordReviewContext]` on the
 * invoice form, the supplier panel, the payment panel), so everything inside it -- the approval
 * block and every [recordField] -- sees the same one, and nothing outside it does.
 */
@Injectable()
export class RecordReviewContext {
  private readonly _task = new BehaviorSubject<Task>(null);
  /** The open approval the viewer is deciding or waiting on; null when there is none. */
  readonly task$ = this._task.asObservable();

  get task(): Task {
    return this._task.value;
  }

  /** The fields under review; empty when the whole record is (or nothing is). */
  get fields(): TaskField[] {
    return this._task.value?.fields || [];
  }

  set(task: Task): void {
    if (task !== this._task.value) { this._task.next(task); }
  }

  isUnderReview(frontendRef: string): boolean {
    return !!frontendRef && this.fields.some(f => f.frontendRef === frontendRef);
  }
}

/**
 * Marks the control that shows one field of a record, by the field's FrontendRef in
 * Admin > Record types:
 *
 *   <ng-select [recordField]="'lines.accountId'" ...>
 *
 * When the approval open on the record is about that field, the control is highlighted so the
 * approver sees exactly what they are being asked about. Every ref used here must be listed in
 * RECORD_FIELD_REFS, which the admin page checks definitions against.
 */
@Directive({
  selector: '[recordField]'
})
export class RecordFieldDirective implements OnInit, OnChanges, OnDestroy {
  @Input() recordField: string;

  private _sub: Subscription;

  constructor(@Optional() private _context: RecordReviewContext, private _el: ElementRef, private _renderer: Renderer2) { }

  ngOnInit(): void {
    if (this._context) {
      this._sub = this._context.task$.subscribe(() => this.apply());
    }
  }

  ngOnChanges(): void {
    this.apply();
  }

  ngOnDestroy(): void {
    this._sub?.unsubscribe();
  }

  private apply(): void {
    const on = !!this._context?.isUnderReview(this.recordField);
    if (on) {
      this._renderer.addClass(this._el.nativeElement, 'record-field--review');
    } else {
      this._renderer.removeClass(this._el.nativeElement, 'record-field--review');
    }
  }
}

/**
 * Every [recordField] reference the side panels carry, by record type code. A field definition
 * whose FrontendRef is not listed here has nothing to highlight, which Admin > Record types warns
 * about. Add the ref here when you put [recordField] on a new control.
 */
export const RECORD_FIELD_REFS: { [recordType: string]: string[] } = {
  invoice: [
    'supplier', 'invoiceNumber', 'date', 'dueDate', 'currency', 'type', 'amountDue',
    'lines.description', 'lines.accountId', 'lines.taxId', 'lines.tracking', 'lines.amount'
  ],
  supplier: [
    'name', 'email', 'taxNumber', 'defaultAccount', 'currency', 'bsb', 'bankAccountNumber'
  ],
  payment: [
    'paymentName', 'account', 'total', 'lines.amount', 'lines.reference'
  ]
};

/** The side panels a task can open in (RecordTypes.PanelKey). */
export const RECORD_PANEL_KEYS = ['invoice', 'supplier', 'payment'];

/**
 * Opens a record's side panel for a task. Implemented by BookkeepingModule, which declares the
 * panels (RecordPanelService); a page without it falls back to the record's route.
 */
export abstract class RecordPanelOpener {
  abstract open(task: Pick<Task, 'panelKey' | 'recordId' | 'recordType'>): boolean;
}
